import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import apiService from '../../services/ApiService';
import type { BuildNotification } from '../../types/buildExpansion';
import { NOTIFICATION_PREFERENCES_PATH } from './NotificationCenter';

const timeAgo = (iso: string): string => {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T')).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Full notification history at /notifications.
 */
const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<BuildNotification[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'events' | 'builds' | 'clubs' | 'passport'>('all');

  const load = useCallback(async (targetPage: number, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getNotifications(targetPage);
      setItems((prev) => (append ? [...prev, ...result.items] : result.items));
      setPage(result.page || targetPage);
      setTotal(result.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(1, false);
  }, [load]);

  const openItem = async (item: BuildNotification) => {
    if (!item.is_read) {
      try {
        await apiService.markNotificationsRead([item.id]);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      } catch {
        // still navigate
      }
    }
    const dest = item.destination_url;
    if (!dest) return;
    if (dest.startsWith('http')) {
      window.location.assign(dest);
      return;
    }
    navigate(dest);
  };

  const filtered = items.filter((item) => {
    if (filter === 'all') return true;
    const cat = (item as BuildNotification & { category?: string }).category;
    const type = item.activity_type || '';
    if (filter === 'events') return cat === 'events' || cat === 'organizer' || type.includes('event');
    if (filter === 'builds') return cat === 'builds' || ['modification_added', 'album_added', 'dyno_recorded', 'award_won'].includes(type);
    if (filter === 'clubs') return cat === 'clubs' || type === 'club_post';
    if (filter === 'passport') return cat === 'passport' || type.startsWith('passport_');
    return true;
  });

  return (
    <PageShell>
      <PageBackLink label="Back to Home" to="/" className="mb-4" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <PageHeader
          eyebrow="Activity"
          title="Notifications"
          description="Important updates across events, builds, clubs, and Passport."
        />
        <Link
          to={NOTIFICATION_PREFERENCES_PATH}
          className="text-sm text-orange-300 hover:text-orange-200 underline-offset-2 hover:underline"
        >
          Notification preferences
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label="Filter notifications">
        {([
          ['all', 'All'],
          ['events', 'Events'],
          ['builds', 'Builds'],
          ['clubs', 'Clubs'],
          ['passport', 'Passport'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={filter === id}
            onClick={() => setFilter(id)}
            className={`rounded-full px-3 py-1.5 text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
              filter === id
                ? 'border-orange-500/50 bg-orange-500/15 text-orange-100'
                : 'border-white/10 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="space-y-3" role="status" aria-busy="true">
          <span className="sr-only">Loading notifications…</span>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-[#141414]/70 animate-pulse motion-reduce:animate-none" />
          ))}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="alert-error mb-4">
          {error}
          <div className="mt-3">
            <Button type="button" variant="secondary" size="sm" onClick={() => void load(1, false)}>
              Try again
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && !error && filtered.length === 0 ? (
        <EmptyState
          title="You're all caught up"
          description="When something needs your attention, it will show up here."
        />
      ) : null}

      <ul className="space-y-2">
        {filtered.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => void openItem(item)}
              className={`w-full text-left rounded-xl border px-4 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
                item.is_read
                  ? 'border-white/[0.06] bg-black/20 hover:bg-black/30'
                  : 'border-orange-500/25 bg-orange-500/[0.06] hover:bg-orange-500/10'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-100">{item.title}</p>
                  {item.body ? <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{item.body}</p> : null}
                </div>
                <span className="shrink-0 text-xs text-zinc-500">{timeAgo(item.created_at)}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {items.length < total ? (
        <div className="mt-6 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={() => void load(page + 1, true)}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </PageShell>
  );
};

export default NotificationsPage;
